import React, { useEffect, useState } from 'react';
import { DocumentIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { classNames } from 'utils/styleUtils';
import { isProjectNameValid } from 'utils/formatUtils';
import {
  useGenerateDataProductSpecificationMutation,
  useGenerateSoleFromDDLMutation,
  useCicdMutation,
  useGetSnowflakeAccountsQuery,
  useGetSnowflakeAccountUsedQuery,
} from 'app/createApi';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import {
  setBranch,
  selectSelectedGroup,
  selectSelectedProject,
  selectCreateNewProject,
  selectNewProjectName,
  setProjectPath,
  selectProjectsCreatedInCurrentSession,
  setProjectsCreatedInCurrentSession,
} from 'app/sharedSlice';
import {
  selectSnowflakeAccountLabel,
  selectSnowflakeUsername,
  selectSnowflakeWarehouse,
  selectSnowflakeRole,
  selectSnowflakeAccountConnected,
  selectSnowflakePrivateKey,
  selectSnowflakePassphrase,
  setSnowflakeUsername,
  setSnowflakeWarehouse,
  setSnowflakeRole,
  setSnowflakeAccountConnected,
  setSnowflakePrivateKey,
  setSnowflakePassphrase,
  setSnowflakeAccount,
  setSnowflakeAccountLabel,
  selectSnowflakeAccount,
} from 'app/snowflakeSlice';
import {
  selectFlow,
  setDataProductName,
  setFlow,
  setSoleOutput,
} from '../../data-product-builder/reducers/builderSlice';
import StepWrapper from 'components/StepWrapper';
import SnowflakeAccountForm from 'components/SnowflakeAccountForm';
import LoadingAndErrorSection from 'components/LoadingAndErrorSection';
import GroupAndProjectSelector from 'components/GroupAndProjectSelector';
import DividerWithText from 'components/DividerWithText';

export interface MetadataImportStepProps {
  onBack: () => void;
  onContinue: () => void;
  skipGrants: boolean;
  onSkipGrantsChange: (value: boolean) => void;
}

export default function TargetProjectStep(props: MetadataImportStepProps): JSX.Element {
  const dispatch = useAppDispatch();

  const [ddlStatement, setDdlStatement] = useState<string>('');
  const [filename, setFilename] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('Failed to create project');
  const flowSectionRef = React.useRef<HTMLDivElement>(null);

  // Example DDL statement
  const exampleDdlStatement = `create or replace database A_CICD;
create or replace schema A_CICD.SERVICE COMMENT='Customer Services';
create or replace TABLE A_CICD.SERVICE.CUSTOMER (
	C_CUSTKEY NUMBER(38,0) NOT NULL,
	C_NAME VARCHAR(25),
	C_ADDRESS VARCHAR(40),
	C_NATIONKEY NUMBER(38,0),
	C_PHONE VARCHAR(15),
	C_ACCTBAL NUMBER(12,2),
	C_MKTSEGMENT VARCHAR(10),
	C_COMMENT VARCHAR(117)
);
create or replace TABLE A_CICD.SERVICE.LINEITEM (
	L_ORDERKEY NUMBER(38,0),
	L_PARTKEY NUMBER(38,0),
	L_SUPPKEY NUMBER(38,0),
	L_LINENUMBER NUMBER(38,0),
	L_QUANTITY NUMBER(12,2),
	L_EXTENDEDPRICE NUMBER(12,2),
	L_DISCOUNT NUMBER(12,2),
	L_TAX NUMBER(12,2),
	L_RETURNFLAG VARCHAR(1),
	L_LINESTATUS VARCHAR(1),
	L_SHIPDATE DATE,
	L_COMMITDATE DATE,
	L_RECEIPTDATE DATE,
	L_SHIPINSTRUCT VARCHAR(25),
	L_SHIPMODE VARCHAR(10),
	L_COMMENT VARCHAR(44)
);
create or replace TABLE A_CICD.SERVICE.ORDERS (
	O_ORDERKEY NUMBER(38,0) NOT NULL,
	O_CUSTKEY NUMBER(38,0),
	O_ORDERSTATUS VARCHAR(1),
	O_TOTALPRICE NUMBER(12,2),
	O_ORDERDATE DATE,
	O_ORDERPRIORITY VARCHAR(15),
	O_CLERK VARCHAR(15),
	O_SHIPPRIORITY NUMBER(38,0),
	O_COMMENT VARCHAR(79)
);
create or replace view A_CICD.SERVICE.V_CUSTOMER(
	C_CUSTKEY,
	C_NAME,
	C_ADDRESS,
	C_NATIONKEY,
	C_PHONE,
	C_ACCTBAL,
	C_MKTSEGMENT,
	C_COMMENT
) as SELECT * FROM rel(table.CUSTOMER);
CREATE OR REPLACE PROCEDURE A_CICD.SERVICE.GET_ACCT_BAL("CID" NUMBER(12,2))
RETURNS TABLE ("CID" NUMBER(38,0), "BALANCE" NUMBER(12,2))
LANGUAGE SQL
COMMENT='user-defined procedure'
EXECUTE AS CALLER
AS 'declare
res RESULTSET DEFAULT (SELECT C_CUSTKEY, C_ACCTBAL FROM CUSTOMER WHERE C_CUSTKEY=:CID);
 BEGIN
  RETURN TABLE(res);
 END';`;

  const flow = useAppSelector(selectFlow);
  const snowflakeAccountLabel = useAppSelector(selectSnowflakeAccountLabel);
  const snowflakeAccount = useAppSelector(selectSnowflakeAccount);
  const snowflakeUsername = useAppSelector(selectSnowflakeUsername);
  const snowflakeWarehouse = useAppSelector(selectSnowflakeWarehouse);
  const snowflakeRole = useAppSelector(selectSnowflakeRole);
  const snowflakePrivateKey = useAppSelector(selectSnowflakePrivateKey);
  const snowflakePassphrase = useAppSelector(selectSnowflakePassphrase);
  const snowflakeAccountConnected = useAppSelector(selectSnowflakeAccountConnected);

  const selectedGroup = useAppSelector(selectSelectedGroup);
  const selectedProject = useAppSelector(selectSelectedProject);
  const createNewProject = useAppSelector(selectCreateNewProject);
  const newProjectName = useAppSelector(selectNewProjectName);
  const projectsCreatedInCurrentSession = useAppSelector(selectProjectsCreatedInCurrentSession);

  const [generateDataProductSpecification] = useGenerateDataProductSpecificationMutation();
  const [generateSoleFromDDL] = useGenerateSoleFromDDLMutation();
  const [cicd] = useCicdMutation();

  const { data: snowflakeAccounts } = useGetSnowflakeAccountsQuery({});
  const { data: previouslySelectedSnowflakeAccount } = useGetSnowflakeAccountUsedQuery(
    { projectPath: selectedProject?.path ?? '' },
    {
      skip: selectedProject === null || selectedProject.path === '',
    },
  );

  useEffect(() => {
    if (
      snowflakeAccounts !== undefined &&
      previouslySelectedSnowflakeAccount !== undefined &&
      Object.keys(snowflakeAccounts).length > 0 &&
      previouslySelectedSnowflakeAccount in snowflakeAccounts
    ) {
      dispatch(setSnowflakeAccount(String(previouslySelectedSnowflakeAccount)));
      dispatch(setSnowflakeAccountLabel(snowflakeAccounts[previouslySelectedSnowflakeAccount]?.account));
      dispatch(setSnowflakeAccountConnected(true));
    }
  }, [snowflakeAccounts, previouslySelectedSnowflakeAccount]);

  const snowflakeOptionCompleted = flow === 'snowflake' && snowflakeAccountConnected;
  const ddlOptionCompleted =
    (flow === 'ddl' || flow === 'example-ddl') && ddlStatement !== '' && snowflakeAccountConnected;

  // Check if DDL contains CREATE DATABASE statement
  const containsCreateDatabaseStatement = (ddl: string): boolean => {
    const lowerCaseDdl = ddl.toLowerCase();
    return lowerCaseDdl.includes('create database') || lowerCaseDdl.includes('create or replace database');
  };

  // Extract database name from DDL statement and remove CREATE DATABASE statements
  const extractDatabaseName = (ddl: string): string => {
    const lowerCaseDdl = ddl.toLowerCase();
    let databaseName = 'TEST'; // Default fallback value

    // Regular expressions to match different CREATE DATABASE patterns
    const createDbRegex = /create\s+database\s+([a-zA-Z0-9_]+)/i;
    const createOrReplaceDbRegex = /create\s+or\s+replace\s+database\s+([a-zA-Z0-9_]+)/i;

    // Try to match with "create or replace database" first (more specific)
    const orReplaceMatch = lowerCaseDdl.match(createOrReplaceDbRegex);
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, @typescript-eslint/strict-boolean-expressions
    if (orReplaceMatch !== null && orReplaceMatch[1] !== null && orReplaceMatch[1] !== undefined) {
      databaseName = orReplaceMatch[1];
      return databaseName;
    }

    // Then try with simple "create database"
    const createMatch = ddl.match(createDbRegex);
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, @typescript-eslint/strict-boolean-expressions
    if (createMatch !== null && createMatch[1] !== null && createMatch[1] !== undefined) {
      databaseName = createMatch[1];
      return databaseName;
    }

    return databaseName;
  };

  // Remove CREATE DATABASE statements from DDL
  const removeCreateDatabaseStatements = (ddl: string): string => {
    // Regular expressions to match different CREATE DATABASE patterns
    const createDbRegex = /create\s+database\s+([a-zA-Z0-9_]+)(\s*;)?/i;
    const createOrReplaceDbRegex = /create\s+or\s+replace\s+database\s+([a-zA-Z0-9_]+)(\s*;)?/i;

    // Remove "create or replace database" statements
    let cleanedDdl = ddl.replace(createOrReplaceDbRegex, '');
    // Remove "create database" statements
    cleanedDdl = cleanedDdl.replace(createDbRegex, '');

    return cleanedDdl;
  };

  const onContinue = (): void => {
    if (!createNewProject) {
      props.onContinue();
    }

    // Check for CREATE DATABASE statement if using DDL flow
    if ((flow === 'ddl' || flow === 'example-ddl') && !containsCreateDatabaseStatement(ddlStatement)) {
      setLoading(false);
      setFailed(true);
      setErrorMessage('CREATE DATABASE statement is mandatory in the DDL script');
      return;
    }

    setLoading(true);
    setFailed(false);
    let projectName = '';
    if (createNewProject) {
      if (newProjectName === '') return;

      if (!isProjectNameValid(newProjectName)) {
        setFailed(true);
        setErrorMessage(
          'The project name is invalid. Project names must start and end with a letter or digit, avoid consecutive special characters, and include only letters, digits, underscores, dots, dashes, or spaces.',
        );
        setLoading(false);
        return;
      }

      const newProjectPath = `${selectedGroup.path}/${newProjectName}`;
      console.log('New project path:', newProjectPath);
      dispatch(setProjectPath(newProjectPath));
      projectName = newProjectPath;
    } else {
      if (selectedProject.name === '') return;
      console.log('Selected project: ', selectedProject);
      dispatch(setProjectPath(selectedProject.path));
      projectName = selectedProject.path;
    }

    const dpName = projectName.split('/').pop() as string;
    dispatch(setDataProductName(dpName));

    const projectAlredyCreatedInThisSession = projectsCreatedInCurrentSession.includes(projectName);
    const isNewProject = createNewProject && !projectAlredyCreatedInThisSession;

    generateDataProductSpecification({
      projectName,
      isNewProject,
      name: dpName,
      description: '',
      dataQualityTests: '',
      version: '0.1.0',
      owner: '',
      group: selectedGroup.path,
      branch: flow === 'ddl' || flow === 'example-ddl' ? 'main' : undefined,
    })
      .unwrap()
      .then((res: any) => {
        console.log(res);
        dispatch(setBranch(flow === 'ddl' || flow === 'example-ddl' ? 'main' : res));
        dispatch(setProjectsCreatedInCurrentSession([...projectsCreatedInCurrentSession, projectName]));

        if (flow === 'snowflake') {
          props.onContinue();
          return;
        }

        generateSoleFromDDL({
          ddl: removeCreateDatabaseStatements(ddlStatement),
        })
          .unwrap()
          .then((res: any) => {
            console.log(res);
            const dbtSole = res;
            dispatch(setSoleOutput(dbtSole));
            cicd({
              dbtSole,
              dataProductName: dpName,
              projectName,
              newDatabaseName: extractDatabaseName(ddlStatement),
              account: snowflakeAccount,
              branch: 'main',
            })
              .unwrap()
              .then((soleRes: any) => {
                console.log(soleRes);
                props.onContinue();
              })
              .catch((soleErr) => {
                console.log(soleErr);
                setFailed(true);
                setErrorMessage('Failed to initialize the project');
              })
              .finally(() => {
                setLoading(false);
              });
          })
          .catch((err) => {
            console.log(err);
            setLoading(false);
            setFailed(true);
            setErrorMessage('Failed to generate Snowflake objects');
          });
      })
      .catch((err) => {
        console.log(err);
        setErrorMessage(err.data);
        setLoading(false);
        setFailed(true);
      });
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files !== null) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target !== null) {
          setDdlStatement(event.target.result as string);
        }
      };
      reader.readAsText(e.target.files[0]);
      setFilename(e.target.files[0].name);
      e.target.value = '';
    }
  };

  return (
    <StepWrapper
      title="Project setup"
      subtitle="Select to have the metadata from an existing Snowflake account through live interaction or from a Data Definition Language (DDL) file that defines a static structure of a database"
      onBack={() => props.onBack()}
      onContinue={() => onContinue()}
      continueDisabled={
        loading ||
        (selectedGroup !== null && selectedGroup.name === '') ||
        (createNewProject && newProjectName === '') ||
        (!createNewProject && selectedProject !== null && selectedProject.name === '') ||
        (createNewProject && !snowflakeOptionCompleted && !ddlOptionCompleted) ||
        (!createNewProject && !snowflakeAccountConnected)
      }
      isLoading={loading}
      isFailed={failed}
      errorMessage={errorMessage}
    >
      <div className="w-full flex flex-col items-center">
        <div className="w-3/4 mx-[auto] mb-4">
          <DividerWithText dividerText="Target project" />
        </div>

        <div className="mx-[auto] w-3/4 pb-2 grid grid-cols-2 gap-6">
          <GroupAndProjectSelector />
        </div>

        {createNewProject && (
          <>
            <div className="w-3/4 mx-[auto]">
              <DividerWithText dividerText="CI/CD source" />
            </div>
            <fieldset className="w-3/4 max-w-[40rem] mx-[auto]">
              <div className="divide-y divide-gray-200">
                <div className="relative flex items-start pb-4 pt-3.5">
                  <div className="min-w-0 flex-1 text-sm leading-6">
                    <label htmlFor="snowflake-option" className="font-medium text-gray-900">
                      Try example DDL
                    </label>
                    <p className="text-gray-500">
                      Get started with an example DDL that includes common database structures and relationships
                    </p>
                  </div>
                  <div className="ml-3 pr-1 flex h-6 items-center">
                    <input
                      id="example-option"
                      name="example-option"
                      type="radio"
                      checked={flow === 'example-ddl'}
                      onClick={() => {
                        dispatch(setFlow('example-ddl'));
                        setDdlStatement(exampleDdlStatement);
                        setFilename('example.sql');
                        setTimeout(() => {
                          flowSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      }}
                      className="w-5 h-5 rounded border-gray-300"
                    />
                  </div>
                </div>
                <div className="relative flex items-start pb-4 pt-3.5">
                  <div className="min-w-0 flex-1 text-sm leading-6">
                    <label htmlFor="ddl-option" className="font-medium text-gray-900">
                      DDL statement
                    </label>
                    <p className="text-gray-500">Get CI/CD source metadata from a DDL file</p>
                  </div>
                  <div className="ml-3 pr-1 flex h-6 items-center">
                    <input
                      id="ddl-option"
                      name="ddl-option"
                      type="radio"
                      checked={flow === 'ddl'}
                      onChange={() => {
                        dispatch(setFlow('ddl'));
                        setDdlStatement('');
                        setFilename(undefined);
                        setTimeout(() => {
                          flowSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      }}
                      className="w-5 h-5 rounded border-gray-300"
                    />
                  </div>
                </div>
                <div className="relative flex items-start pb-4 pt-3.5">
                  <div className="min-w-0 flex-1 text-sm leading-6">
                    <label htmlFor="snowflake-option" className="font-medium text-gray-900">
                      Snowflake
                    </label>
                    <p className="text-gray-500">Get CI/CD source metadata from your Snowflake account</p>
                  </div>
                  <div className="ml-3 pr-1 flex h-6 items-center">
                    <input
                      id="snowflake-option"
                      name="snowflake-option"
                      type="radio"
                      checked={flow === 'snowflake'}
                      onChange={() => {
                        dispatch(setFlow('snowflake'));
                        setTimeout(() => {
                          flowSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      }}
                      className="w-5 h-5 rounded border-gray-300"
                    />
                  </div>
                </div>
              </div>
            </fieldset>

            {flow !== 'not-selected' && (
              <div ref={flowSectionRef} className="w-3/4 mx-[auto] mt-8 mb-6">
                <DividerWithText dividerText={flow === 'snowflake' ? 'Snowflake privileges' : 'DDL'} />
              </div>
            )}

            <div className="w-full flex flex-col items-center">
              {flow === 'snowflake' && (
                <div className="space-y-2 w-full flex flex-col items-center">
                  <div className="w-3/4 mx-[auto] mb-4">
                    <div className="rounded-md bg-blue-50 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <InformationCircleIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
                        </div>
                        <div className="ml-3 flex-1">
                          <h3 className="text-sm font-medium text-blue-800">Required Snowflake Privileges</h3>
                          <div className="mt-2 text-sm text-blue-700">
                            <p className="mb-2">
                              The CI/CD configuration generation process requires a Snowflake ROLE with specific
                              privileges:
                              <br />
                              <span className="ml-4 inline-block">
                                (1) permission to execute GET_DDL operations on the selected objects, and
                              </span>
                              <br />
                              <span className="ml-4 inline-block">
                                (2) access to the SNOWFLAKE.ACCOUNT_USAGE.GRANTS_TO_ROLES view.
                              </span>
                              <br />
                              This view is essential for generating the appropriate permission grants in the
                              configuration.
                            </p>
                            <p className="mb-2">
                              To grant the necessary access to the SNOWFLAKE.ACCOUNT_USAGE.GRANTS_TO_ROLES view, execute
                              the following SQL statement:
                            </p>
                            <code className="block w-full bg-blue-50 border border-blue-200 px-3 py-2 rounded-md font-mono text-sm text-blue-800 shadow-sm overflow-x-auto my-2">
                              <div className="text-center">
                                GRANT IMPORTED PRIVILEGES ON DATABASE SNOWFLAKE TO ROLE {'{user_role}'};
                              </div>
                            </code>
                            <div className="mt-3 flex items-start">
                              <div className="flex h-5 items-center">
                                <input
                                  id="skip-grants-checkbox"
                                  name="skip-grants-checkbox"
                                  type="checkbox"
                                  checked={props.skipGrants}
                                  onChange={(e) => props.onSkipGrantsChange(e.target.checked)}
                                  className="h-4 w-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                                />
                              </div>
                              <div className="ml-2 text-sm">
                                <label htmlFor="skip-grants-checkbox" className="font-medium text-blue-800">
                                  Skip grants generation
                                </label>
                                <p className="text-blue-700">
                                  Check this if you lack permission to query SNOWFLAKE.ACCOUNT_USAGE.GRANTS_TO_ROLES
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {(flow === 'ddl' || flow === 'example-ddl') && (
                <>
                  {flow === 'example-ddl' && (
                    <div className="w-3/4 mx-[auto] mb-4">
                      <div className="rounded-md bg-blue-50 p-4">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <InformationCircleIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
                          </div>
                          <div className="ml-3 flex-1">
                            <h3 className="text-sm font-medium text-blue-800">About the Example DDL</h3>
                            <div className="mt-2 text-sm text-blue-700">
                              <p className="font-medium mb-2">The sample DDL includes:</p>
                              <ul className="list-disc pl-5 space-y-1">
                                <li>
                                  Creates <span className="bg-blue-200 px-1 rounded">A_CICD</span> database with{' '}
                                  <span className="bg-blue-200 px-1 rounded">SERVICE</span> schema
                                </li>
                                <li>
                                  Contains <span className="bg-blue-200 px-1 rounded">CUSTOMER</span>,{' '}
                                  <span className="bg-blue-200 px-1 rounded">ORDERS</span>, and{' '}
                                  <span className="bg-blue-200 px-1 rounded">LINEITEM</span> tables
                                </li>
                                <li>Includes a view for customer data</li>
                                <li>Defines a procedure to retrieve customer account balance</li>
                              </ul>
                              <div className="mt-3 flex items-center">
                                <span className="text-yellow-500 mr-2">⚠️</span>
                                <span className="font-medium">
                                  IMPORTANT: Before running, ensure the database name won&apos;t conflict with an
                                  existing database.
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="w-3/4 mb-4 flex justify-center">
                    <input type="file" id="ddl-file-input" accept=".sql" onChange={onFileChange} className="hidden" />
                    <button
                      type="button"
                      onClick={() => document.getElementById('ddl-file-input')?.click()}
                      className={classNames(
                        'rounded-md px-3 py-2 text-sm font-semibold shadow-sm',
                        filename === undefined
                          ? 'bg-dataops-primary text-white hover:bg-dataops-primary-hover disabled:bg-gray-400'
                          : 'bg-white text-gray-900 hover:bg-gray-50 ring-1 ring-inset ring-gray-300',
                      )}
                    >
                      {filename === undefined ? 'Upload a DDL file' : 'Upload a different DDL file'}
                    </button>
                  </div>

                  <div className="w-3/4 mb-6">
                    <div className="flex justify-between items-center">
                      <label htmlFor="ddl" className="block text-sm font-medium leading-6 text-gray-900">
                        Paste DDL statements (separated by semicolons)
                      </label>
                      {filename !== undefined && (
                        <div className="flex items-center text-sm text-gray-700 font-medium">
                          <DocumentIcon className="h-4 w-4 text-gray-700 mr-1" aria-hidden="true" />
                          <span className="font-semibold">{filename}</span>
                        </div>
                      )}
                    </div>
                    <div className="mt-1">
                      <textarea
                        id="ddl"
                        name="ddl"
                        rows={5}
                        value={ddlStatement}
                        onChange={(e) => setDdlStatement(e.target.value)}
                        className="block w-full rounded-md border-0 p-2 text-sm text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-40"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {(flow !== 'not-selected' || !createNewProject) && (
          <>
            <div className="w-3/4 mx-[auto] mb-6">
              <DividerWithText dividerText="Snowflake connection" />
            </div>
            <div className="text-gray-600 text-sm mb-2 w-3/4 text-center">Connect to your target Snowflake account</div>
            <SnowflakeAccountForm
              snowflakeAccount={snowflakeAccountLabel}
              setSnowflakeAccount={(account) => dispatch(setSnowflakeAccountLabel(account))}
              snowflakeUsername={snowflakeUsername}
              setSnowflakeUsername={(username) => dispatch(setSnowflakeUsername(username))}
              snowflakeWarehouse={snowflakeWarehouse}
              setSnowflakeWarehouse={(warehouse) => dispatch(setSnowflakeWarehouse(warehouse))}
              snowflakeRole={snowflakeRole}
              setSnowflakeRole={(role) => dispatch(setSnowflakeRole(role))}
              snowflakeAccountConnected={snowflakeAccountConnected}
              setSnowflakeAccountConnected={(connected) => dispatch(setSnowflakeAccountConnected(connected))}
              snowflakePrivateKey={snowflakePrivateKey}
              setSnowflakePrivateKey={(key) => dispatch(setSnowflakePrivateKey(key))}
              snowflakePassphrase={snowflakePassphrase}
              setSnowflakePassphrase={(passphrase) => dispatch(setSnowflakePassphrase(passphrase))}
            />
          </>
        )}

        <div className="w-3/4 mx-[auto] mt-4">
          <LoadingAndErrorSection
            isLoading={loading}
            isFailed={failed}
            errorMessage={errorMessage}
            hideLoading={true}
          />
        </div>
      </div>
    </StepWrapper>
  );
}
